/** Shared chip options for ICP targeting — keep in sync with simplified onboarding. */

export interface IcpTargetOption {
  id: string;
  name: string;
}

/** Exact list used by simplified onboarding Target Job Titles. */
export const TARGET_JOB_TITLE_OPTIONS: IcpTargetOption[] = [
  { id: 'CEO', name: 'CEO' },
  { id: 'CTO', name: 'CTO' },
  { id: 'CFO', name: 'CFO' },
  { id: 'COO', name: 'COO' },
  { id: 'VP Sales', name: 'VP Sales' },
  { id: 'VP Marketing', name: 'VP Marketing' },
  { id: 'VP Engineering', name: 'VP Engineering' },
  { id: 'Director', name: 'Director' },
  { id: 'Head of Department', name: 'Head of Department' },
  { id: 'Manager', name: 'Manager' },
  { id: 'Founder', name: 'Founder' },
  { id: 'Other', name: 'Other' },
];

/**
 * Exact department list from the original onboarding Step 4 chips
 * (DEPARTMENT_MAP in onboarding.effects.ts).
 */
export const TARGET_DEPARTMENT_OPTIONS: IcpTargetOption[] = [
  { id: 'Leadership', name: 'Leadership' },
  { id: 'Engineering', name: 'Engineering' },
  { id: 'Sales', name: 'Sales' },
  { id: 'Marketing', name: 'Marketing' },
  { id: 'Finance/Accounting', name: 'Finance/Accounting' },
  { id: 'HR', name: 'HR' },
  { id: 'Operations', name: 'Operations' },
  { id: 'Legal', name: 'Legal' },
  { id: 'Product', name: 'Product' },
  { id: 'Customer Success', name: 'Customer Success' },
  { id: 'IT', name: 'IT' },
  { id: 'Design', name: 'Design' },
  { id: 'Procurement', name: 'Procurement' },
  { id: 'Other', name: 'Other' },
];

/** Map legacy/slug values onto the canonical Step 4 department labels. */
const DEPARTMENT_ALIASES: Record<string, string> = {
  leadership: 'Leadership',
  engineering: 'Engineering',
  sales: 'Sales',
  marketing: 'Marketing',
  finance: 'Finance/Accounting',
  'finance/accounting': 'Finance/Accounting',
  finance_accounting: 'Finance/Accounting',
  financial: 'Finance/Accounting',
  hr: 'HR',
  operations: 'Operations',
  legal: 'Legal',
  product: 'Product',
  'customer success': 'Customer Success',
  customer_success: 'Customer Success',
  support: 'Customer Success',
  it: 'IT',
  design: 'Design',
  procurement: 'Procurement',
  executive: 'Leadership',
  other: 'Other',
};

/** Parse a comma-separated ICP string into a Set, normalizing known option ids. */
export function parseCsvSelections(value: string | null | undefined, options: IcpTargetOption[]): Set<string> {
  const selected = new Set<string>();
  if (!value?.trim()) {
    return selected;
  }

  const optionByLowerId = new Map(options.map(o => [o.id.toLowerCase(), o.id]));

  for (const part of value.split(',')) {
    const trimmed = part.trim();
    if (!trimmed) {
      continue;
    }
    const lower = trimmed.toLowerCase();
    selected.add(
      optionByLowerId.get(lower)
      ?? DEPARTMENT_ALIASES[lower]
      ?? trimmed
    );
  }

  return selected;
}

/**
 * The 'Other' chip id. It is a UI affordance (selecting it reveals the
 * custom free-text input) and is stripped from the saved CSV — the custom
 * entries themselves are what gets stored.
 */
export const OTHER_OPTION_ID = 'Other';

/** Max length of one custom 'Other' entry. */
export const MAX_CUSTOM_TARGET_LENGTH = 255;

export function selectionsToCsv(selected: Set<string>): string {
  return Array.from(selected)
    .filter(value => value.toLowerCase() !== OTHER_OPTION_ID.toLowerCase())
    .join(', ');
}

/** True when the value is one of the predefined chip options. */
export function isPredefinedOption(value: string, options: IcpTargetOption[]): boolean {
  const lower = value.toLowerCase();

  return options.some(o => o.id.toLowerCase() === lower);
}

/** The custom (non-predefined, non-Other) entries in a selection set. */
export function customSelections(selected: Set<string>, options: IcpTargetOption[]): string[] {
  return Array.from(selected).filter(
    value => value.toLowerCase() !== OTHER_OPTION_ID.toLowerCase() && !isPredefinedOption(value, options)
  );
}

/**
 * Sanitize a typed custom entry: trim, strip commas (the storage is a
 * comma-separated string — a comma would split one entry into two), cap at
 * MAX_CUSTOM_TARGET_LENGTH. Returns '' when nothing usable remains.
 */
export function sanitizeCustomTarget(raw: string): string {
  return raw.replace(/,/g, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_CUSTOM_TARGET_LENGTH);
}
